import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Briefcase, FileDown, Archive, ShieldCheck, Clock, AlertTriangle,
} from 'lucide-react';
import { theme, inputStyle, labelStyle } from '../theme';
import { SectionCard, StatCard } from '../components/swipe';

// ═══════════════════════════════════════════════════════════════════
//  InspectorPackagePage — one-click ZIP generator for the
//  Drugs Inspector / NABH assessor / GST officer visit day.
//
//  Lets the user:
//    • pick a date range (default last 90 days)
//    • see what's inside the ZIP before downloading
//    • trigger the download
//    • view a retention audit (how old is the oldest record per
//      collection — the register is immutable, this just flags
//      records eligible for cold-archive)
// ═══════════════════════════════════════════════════════════════════

const CONTENTS = [
  ['00-cover.pdf',               'Legal reference card — clinic info + statutory citations + manifest'],
  ['01-dispensing-register.pdf', 'Immutable dispensing register (D&C Rule 123 / Sch K)'],
  ['02-sample-bills.pdf',        'Representative GST-exempt + taxable invoices'],
  ['03-schedule-summary.pdf',    'Schedule H/H1/X counts + any labels pending print'],
  ['04-certificates.pdf',        'Clinic + staff credentials with expiry status'],
  ['05-committees.pdf',          'NABH committee meeting minutes summary'],
  ['06-patient-safety.pdf',      'Hand hygiene compliance + Surgical safety checklist'],
  ['07-low-stock-alerts.pdf',    'Low-stock + expiring-soon batch alerts'],
];

const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
};
const defaultTo = () => new Date().toISOString().slice(0, 10);

export default function InspectorPackagePage({ API_BASE }) {
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo]     = useState(defaultTo());
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}/compliance/retention-audit`)
      .then(r => setAudit(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API_BASE]);

  const download = () => {
    const params = new URLSearchParams({ from, to });
    // Open in a new tab so the browser handles the zip download natively
    // and axios auth headers propagate via the cookie / saved token.
    window.open(`${API_BASE}/compliance/inspector-ready-package?${params}`, '_blank');
  };

  const oldest = audit?.collections || {};

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>
      <SectionCard variant="white" bordered>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Briefcase size={28} color={theme.primary} />
          <div>
            <h2 style={{ margin: 0, color: theme.navy, fontSize: '1.15rem', fontWeight: 800 }}>Inspector-Ready Package</h2>
            <p style={{ margin: '2px 0 0', color: theme.textMuted, fontSize: '0.78rem' }}>
              One ZIP with every compliance PDF a Drugs Inspector / NABH assessor / GST officer will ask for.
            </p>
          </div>
        </div>
      </SectionCard>

      {/* Date range + download */}
      <SectionCard variant="blue" bordered title="Report period">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10 }}>
          <div>
            <label style={labelStyle}>From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ alignSelf: 'end' }}>
            <button onClick={download} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', background: theme.primary, color: 'white',
              border: 'none', borderRadius: 8, fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', width: '100%',
            }}>
              <FileDown size={16} /> Download ZIP
            </button>
          </div>
        </div>
      </SectionCard>

      {/* Contents preview */}
      <SectionCard variant="white" bordered title="What's inside">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <tbody>
            {CONTENTS.map(([file, desc]) => (
              <tr key={file}>
                <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '0.78rem',
                             color: theme.primary, fontWeight: 700, width: 210, verticalAlign: 'top' }}>{file}</td>
                <td style={{ padding: '6px 10px', color: theme.navy }}>{desc}</td>
              </tr>
            ))}
            <tr><td colSpan={2} style={{ padding: '4px 10px', fontSize: '0.72rem', color: theme.textMuted }}>+ readme.txt — file-by-file manifest</td></tr>
          </tbody>
        </table>
      </SectionCard>

      {/* Retention audit */}
      <SectionCard variant="mint" bordered
        title="Retention audit"
        subtitle="Read-only — the legal register is never hard-deleted. This tells you what is old enough to archive to cold storage.">
        {loading ? (
          <p style={{ color: theme.textMuted }}>Loading…</p>
        ) : !audit ? (
          <p style={{ color: theme.danger }}>Retention audit unavailable.</p>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 8, marginBottom: 10 }}>
              {[
                ['Dispensing register', oldest.dispensingRecords],
                ['Bills',               oldest.bills],
                ['Hand-hygiene audits', oldest.handHygieneAudits],
                ['Surgical checklists', oldest.surgicalSafetyChecklists],
                ['Committee meetings',  oldest.committeeMeetings],
              ].map(([label, data]) => {
                const oldestDate = data?.oldest ? new Date(data.oldest) : null;
                const ageYears = oldestDate ? ((Date.now() - oldestDate.getTime()) / (365 * 24 * 3600 * 1000)).toFixed(1) : '—';
                return (
                  <StatCard
                    key={label}
                    title={label}
                    value={data?.total ?? 0}
                    sub={oldestDate ? `Oldest ${ageYears} yrs` : 'No records yet'}
                    accent={data?.olderThan5y > 0 ? '#b45309' : '#10b981'}
                  />
                );
              })}
            </div>

            {audit.policy && (
              <div style={{ marginTop: 8, padding: 10, background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#78350f', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ShieldCheck size={13} /> Retention policy
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.75rem', color: '#78350f', lineHeight: 1.55 }}>
                  {Object.entries(audit.policy).map(([k, v]) => (
                    <li key={k}><strong>{k}</strong>: {v}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </SectionCard>

      <div style={{ marginTop: 14, padding: 12, background: '#fef3c7', border: `1px solid #fde68a`, borderRadius: 10, fontSize: '0.78rem', color: '#78350f' }}>
        <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        <strong>Tip:</strong> download a fresh package the morning of every assessor visit.
        Each PDF inside the ZIP already carries its own statutory citation footer,
        so even individually they're audit-defensible.
      </div>
    </div>
  );
}
